import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // Mock notifications
    useEffect(() => {
        const mockNotifications = [
            {
                id: '1',
                type: 'warning',
                title: 'Delivery Delayed',
                message: 'Job JOB003 is delayed by 2 hours due to traffic',
                timestamp: new Date(Date.now() - 300000), // 5 mins ago
                read: false,
                actionUrl: '/jobs/JOB003'
            },
            {
                id: '2',
                type: 'success',
                title: 'Job Completed',
                message: 'Container CONT789 delivered successfully',
                timestamp: new Date(Date.now() - 3600000), // 1 hour ago
                read: false,
                actionUrl: '/jobs/JOB002'
            },
            {
                id: '3',
                type: 'info',
                title: 'New Job Assigned',
                message: 'Job JOB005 assigned to vehicle MH-01-AB-1234',
                timestamp: new Date(Date.now() - 7200000), // 2 hours ago
                read: true,
                actionUrl: '/jobs/JOB005'
            }
        ];

        setNotifications(mockNotifications);
        setUnreadCount(mockNotifications.filter(n => !n.read).length);
    }, []);

    // Simulate real-time notifications
    useEffect(() => {
        const interval = setInterval(() => {
            // Random chance to add a new notification
            if (Math.random() < 0.1) { // 10% chance every 30 seconds
                const newNotification = {
                    id: Date.now().toString(),
                    type: ['info', 'warning', 'success'][Math.floor(Math.random() * 3)],
                    title: 'New Update',
                    message: 'A new event has occurred in your system',
                    timestamp: new Date(),
                    read: false,
                    actionUrl: '/dashboard'
                };

                addNotification(newNotification);

                // Show toast notification
                toast.success(newNotification.message, {
                    duration: 4000,
                    position: 'top-right'
                });
            }
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, []);

    const addNotification = (notification) => {
        setNotifications(prev => [notification, ...prev]);
        if (!notification.read) {
            setUnreadCount(prev => prev + 1);
        }
    };

    const markAsRead = (notificationId) => {
        setNotifications(prev =>
            prev.map(notification =>
                notification.id === notificationId
                    ? { ...notification, read: true }
                    : notification
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllAsRead = () => {
        setNotifications(prev =>
            prev.map(notification => ({ ...notification, read: true }))
        );
        setUnreadCount(0);
    };

    const removeNotification = (notificationId) => {
        const notification = notifications.find(n => n.id === notificationId);
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        if (notification && !notification.read) {
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
    };

    const showToast = (type, message, options = {}) => {
        const toastOptions = {
            duration: 4000,
            position: 'top-right',
            ...options
        };

        switch (type) {
            case 'success':
                toast.success(message, toastOptions);
                break;
            case 'error':
                toast.error(message, toastOptions);
                break;
            case 'warning':
                toast(message, { ...toastOptions, icon: '⚠️' });
                break;
            default:
                toast(message, toastOptions);
        }
    };

    const value = {
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        showToast
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationProvider;